import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Mail, Lock, Eye, EyeOff, Shield, ArrowLeft, RefreshCw, AlertCircle, Key } from "lucide-react";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";

const MYEUROPACIFIC_LOGO = "https://customer-assets.emergentagent.com/job_65c103ce-390f-4bae-b6d5-2798d8bc6281/artifacts/6in60tne_image.png";
const ROKET_IMAGE = "https://customer-assets.emergentagent.com/job_6e94cf61-ed07-442f-8a8f-1e142db37be8/artifacts/bvtijyq3_IMG_6616.jpg";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials"); // "credentials" or "2fa"
  const [otpCode, setOtpCode] = useState(["", "", "", "", "", ""]);
  const [resending, setResending] = useState(false);
  const [showPukModal, setShowPukModal] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const otpRefs = useRef([]);
  const { login, setAuthData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If the user was bounced here by ProtectedRoute, remember where they came from.
  // We only auto-return for the mobile/PWA sections (otherwise default flows stay).
  const fromPath = location.state?.from;
  const isPwaReturn = typeof fromPath === "string" && (
    fromPath.startsWith("/staff-mobile") || fromPath.startsWith("/mobile")
  );

  const redirectAfterAuth = (userData) => {
    if (isPwaReturn) {
      navigate(fromPath, { replace: true });
      return;
    }
    if (userData?.user_type === "customer") {
      navigate("/customer-portal");
    } else if (userData?.user_type === "partner") {
      navigate("/partner-portal");
    } else {
      navigate("/search");
    }
  };

  // Focus first OTP input when step changes to 2FA
  useEffect(() => {
    if (step === "2fa" && otpRefs.current[0]) {
      otpRefs.current[0].focus();
    }
  }, [step]);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      
      if (response.data.requires_2fa) {
        setStep("2fa");
        toast.success("Verification code sent to your email");
      } else if (response.data.access_token) {
        // 2FA disabled - direct token response
        const { access_token, user: userData } = response.data;
        localStorage.setItem("token", access_token);
        setAuthData(access_token, userData);
        toast.success("Welcome back!");
        // Redirect based on user type (or back to PWA section if launched there)
        redirectAfterAuth(userData);
      }
    } catch (error) {
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      
      // Show PUK modal after first failed attempt with "Invalid credentials"
      if (error.response?.data?.detail === "Invalid credentials" || 
          error.response?.status === 401) {
        setShowPukModal(true);
      }
      
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace - go to previous input
    if (e.key === "Backspace" && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtpCode(digits);
      otpRefs.current[5]?.focus();
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    const code = otpCode.join("");
    
    if (code.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/verify-2fa`, { 
        email, 
        code 
      });
      
      // Login successful - set token and user directly
      const { access_token, user: userData } = response.data;
      localStorage.setItem("token", access_token);
      
      // Use the setAuthData function from AuthContext to set user without re-triggering 2FA
      setAuthData(access_token, userData);
      
      toast.success("Welcome back!");
      // Redirect based on user type (or back to PWA section if launched there)
      redirectAfterAuth(userData);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
      setOtpCode(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/auth/resend-2fa`, { email, password });
      toast.success("New verification code sent");
      setOtpCode(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } catch (error) {
      toast.error("Failed to resend code");
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    setStep("credentials");
    setOtpCode(["", "", "", "", "", ""]);
  };

  return (
    <>
      {/* Password Reset Assistance Modal */}
      <AnimatePresence>
        {showPukModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPukModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Having trouble logging in?</h3>
                  <p className="text-sm text-gray-500">We're here to help</p>
                </div>
              </div>
              
              {/* Content */}
              <div className="space-y-4 mb-6">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>System Update Notice:</strong> We've recently implemented security enhancements to improve your experience. As part of these updates, you may need to reset your password.
                  </p>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Key className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Reset with PUK Code</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Use your Personal Unlock Key (PUK) to securely reset your password. If you don't have your PUK, please contact your administrator.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowPukModal(false)}
                >
                  Try Again
                </Button>
                <Button
                  className="flex-1 bg-[#003366] hover:bg-[#004488]"
                  onClick={() => navigate("/emergency-reset")}
                >
                  <Key className="h-4 w-4 mr-2" />
                  Reset with PUK
                </Button>
              </div>
              
              {/* Footer link */}
              <div className="mt-4 text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-gray-500 hover:text-[#003366] underline"
                >
                  Or use email recovery instead
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left - Branding with Roket */}
      <div className="hidden lg:flex bg-gradient-to-br from-[#003366] via-[#004488] to-[#002244] items-center justify-center p-12 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 left-10 w-32 h-32 bg-blue-400/10 rounded-full blur-xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-48 h-48 bg-cyan-400/10 rounded-full blur-xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.4, 0.6, 0.4] }}
            transition={{ duration: 5, repeat: Infinity }}
          />
          <motion.div
            className="absolute top-1/2 left-1/4 w-24 h-24 bg-amber-400/10 rounded-full blur-xl"
            animate={{ y: [-20, 20, -20], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 3, repeat: Infinity }}
          />
        </div>
        
        <div className="text-center relative z-10">
          {/* Europacific Logo */}
          <motion.div
            className="relative inline-block mb-8"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="w-28 h-28 rounded-full bg-white/10 backdrop-blur shadow-2xl mx-auto flex items-center justify-center p-4"
              animate={{ 
                boxShadow: [
                  "0 0 20px rgba(255, 255, 255, 0.1)",
                  "0 0 40px rgba(255, 255, 255, 0.2)",
                  "0 0 20px rgba(255, 255, 255, 0.1)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              whileHover={{ scale: 1.05 }}
            >
              <img 
                src="/europacific-logo.jpg" 
                alt="Europacific Logo" 
                className="w-full h-full object-contain"
              />
            </motion.div>
          </motion.div>
          
          <motion.h2 
            className="text-3xl font-bold text-white mb-2"
            style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <span className="font-light italic">my</span>Europacific
          </motion.h2>
          
          <motion.p 
            className="text-white/60 italic text-sm mb-6"
            style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Logistics simplified
          </motion.p>
          
          <motion.div
            className="bg-white/10 backdrop-blur rounded-xl px-6 py-4 max-w-sm mx-auto"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <p className="text-white/80 text-sm">
              Sign in to track shipments, manage quotes, and streamline your logistics operations.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo - Europacific */}
          <div className="lg:hidden flex flex-col items-center justify-center mb-8">
            <motion.div
              className="w-20 h-20 rounded-full bg-white shadow-lg mb-3 flex items-center justify-center p-3"
              animate={{ boxShadow: ["0 0 10px rgba(0, 51, 102, 0.1)", "0 0 20px rgba(0, 51, 102, 0.2)", "0 0 10px rgba(0, 51, 102, 0.1)"] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <img 
                src="/europacific-logo.jpg" 
                alt="Europacific Logo" 
                className="w-full h-full object-contain"
              />
            </motion.div>
            <span className="text-[#003366] text-xl font-light italic" style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
              my<span className="font-semibold">Europacific</span>
            </span>
          </div>

          <AnimatePresence mode="wait">
            {step === "credentials" ? (
              <motion.div
                key="credentials"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="card-surface p-8"
              >
                <div className="mb-8">
                  <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
                    Welcome Back
                  </h1>
                  <p className="text-muted-foreground">
                    Sign in to your account to continue tracking
                  </p>
                </div>

                <form onSubmit={handleCredentialsSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-foreground">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input-europacific pl-10"
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-foreground">
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="input-europacific pl-10 pr-10"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="btn-primary w-full h-12"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </Link>
                  <span className="mx-2 text-muted-foreground">|</span>
                  <Link
                    to="/emergency-reset"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    data-testid="puk-reset-link"
                  >
                    Reset with PUK
                  </Link>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-muted-foreground">
                    Don't have an account?{" "}
                    <Link
                      to="/register"
                      className="text-primary hover:underline font-medium"
                      data-testid="register-link"
                    >
                      Create Account
                    </Link>
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="2fa"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="card-surface p-8"
              >
                <button
                  onClick={handleBackToLogin}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </button>

                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
                    Two-Factor Authentication
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    We've sent a 6-digit code to<br />
                    <span className="font-medium text-foreground">{email}</span>
                  </p>
                </div>

                <form onSubmit={handle2FASubmit} className="space-y-6">
                  <div className="space-y-3">
                    <Label className="text-foreground text-center block">
                      Enter verification code
                    </Label>
                    <div 
                      className="flex justify-center gap-2"
                      onPaste={handleOtpPaste}
                    >
                      {otpCode.map((digit, index) => (
                        <Input
                          key={index}
                          ref={(el) => (otpRefs.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          className="w-12 h-14 text-center text-2xl font-bold border-2 focus:border-primary"
                          data-testid={`otp-input-${index}`}
                        />
                      ))}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="btn-primary w-full h-12"
                    disabled={loading || otpCode.join("").length !== 6}
                    data-testid="verify-2fa-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      "Verify & Sign In"
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-muted-foreground text-sm mb-2">
                    Didn't receive the code?
                  </p>
                  <button
                    onClick={handleResendCode}
                    disabled={resending}
                    className="text-primary hover:underline font-medium text-sm flex items-center gap-2 mx-auto"
                  >
                    {resending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    Resend Code
                  </button>
                </div>

                <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700 text-center">
                    Code expires in 10 minutes. Check your spam folder if you don't see the email.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
    </>
  );
}

export { LoginPage };
